import { Middleware } from 'redux'
import { Action, getType } from 'typesafe-actions'
import {fetchCarlineData, fetchCarlineFiltersData} from '../../actions/app/carlineData.actions'
import { fetchCart } from '../../actions/app/cart.actions'
import { fetchEfficiencyData } from '../../actions/app/efficiencyData.actions'
import { fetchEntryData } from '../../actions/app/entryData.actions'
import { fetchEquipmentGroups } from '../../actions/app/equipmentGroups.actions'
import {
    addFailedRequestActionToState,
    removeFailedRequestActionFromState,
} from '../../actions/app/failedRequests.actions'
import { createNotification } from '../../actions/app/notification.actions'
import { resetConfigurationProcess } from '../../actions/app/reset.actions'
import { loadConfigurationByVehicleCode } from '../../actions/app/vehicleCode.actions'
import { fetchVisualization } from '../../actions/app/visualization.actions'
import { isApiFailureAction, isApiRequestAction } from '../../apiRequest'
import { isActionOf } from 'typesafe-actions'
import failedRequestsSelector from '../../selectors/failedRequestsSelector'
import { storeUserGarageVehicle } from '../../actions/app/userGarage.actions'
import { loadConfigurationByVin } from '../../actions/app/vin.actions'
import { updateUserAfterRefresh, triggerE3DReinit, logout as logoutAction } from '../../actions/app/e3DGrpAuth.actions'
import { refreshTokens } from '../../../services/e3DAuthService'
import getOAuthClient from '../../../services/e3DAuthService'
import { grpAuthStateSelector, isGrpLoggedInSelector } from '../../selectors/e3DGrpAuthSelectors'
import { logger } from '../../../utils/dccLogger'
import is3DExhibitionEnabledSelector from '../../selectors/feature-scope/is3DExhibitionEnabledSelector'
import { sessionIdManager } from '../../../utils/sessionIdManager'

const SESSION_EXPIRED = 'SESSION_EXPIRED'
const SESSION_COOKIE_MISSING = 'SESSION_COOKIE_MISSING'
const VEHICLE_CODE_NOT_FOUND = 'VEHICLE_CODE_NOT_FOUND'
const VIN_NOT_FOUND = 'VIN_NOT_FOUND'

// because of back-end endpoint which is void => it has empty response body
const UNEXPECTED_END_OF_JSON_INPUT1 = 'Unexpected end of JSON input'
const UNEXPECTED_END_OF_JSON_INPUT2 = 'unexpected end of data at line 1 column 1 of the JSON data'

const attemptTokenRefreshOnAuthError = async (store: any, status?: number): Promise<boolean> => {
    const { getState, dispatch } = store

    if (![401, 403].includes(status || 0)) {
        return false
    }

    const state = getState()
    const is3DExhibitionEnabled = is3DExhibitionEnabledSelector(state)
    const isGrpAuthenticated = isGrpLoggedInSelector(state)
    const e3DGrpAuthState = grpAuthStateSelector(state)

    if (!is3DExhibitionEnabled || !isGrpAuthenticated || !e3DGrpAuthState.user) {
        return false
    }

    try {
        logger.info(`Authentication error (${status}), attempting token refresh...`)
        const oAuthClient = getOAuthClient()

        if (!oAuthClient) {
            logger.warn('OAuth client not available for token refresh on auth error')
            return false
        }

        const refreshResult = await refreshTokens(oAuthClient, e3DGrpAuthState.user, e3DGrpAuthState)

        if (refreshResult.success && refreshResult.updatedUser) {
            dispatch(updateUserAfterRefresh(refreshResult.updatedUser))
            dispatch(triggerE3DReinit())
            logger.info('Token refresh on auth error completed successfully')
            return true
        } else {
            logger.warn('Token refresh on auth error failed', { terminal: refreshResult.terminal })

            if (refreshResult.terminal) {
                logger.warn('Terminal error on auth failure refresh. Dispatching logout.')
                dispatch(logoutAction())
            }

            return false
        }
    } catch (error) {
        logger.error(`Error during token refresh on auth error: ${error}`)
        return false
    }
}

const errorMiddleware: Middleware = (store) => (next) => (action) => {
    next(action)

    if (isActionOf(triggerE3DReinit, action)) {
        if (typeof (window as any).__e3dReinitCallback === 'function') {
            (window as any).__e3dReinitCallback()
            logger.info('E3D reinit triggered from middleware')
        }
        return
    }

    const getNotificationMessage = (type: string, message: string, causedBy: Action): string => {
        if (message === SESSION_EXPIRED || message === SESSION_COOKIE_MISSING) {
            return 'error.sessionExpired'
        }

        if (causedBy.type === getType(loadConfigurationByVehicleCode)) {
            if (message === VEHICLE_CODE_NOT_FOUND) {
                return 'vehicleCode.audi.notFound'
            }
        }

        if (causedBy.type === getType(loadConfigurationByVin)) {
            if (message === VIN_NOT_FOUND) {
                return `vin.notFound`
            }
        }

        if (causedBy.type === getType(storeUserGarageVehicle)) {
            if (message === 'CONFIGURATION_LIMIT_EXCEEDED') {
                return 'garage.userGarage.configurationLimitExceeded'
            }
        }

        if (type === 'fetch') {
            return 'error.fetch'
        }

        return 'error.general'
    }

    if (isApiRequestAction(action)) {
        const { getState, dispatch } = store
        const { causedBy } = action.meta
        const failedRequests = failedRequestsSelector(getState())

        if (failedRequests.some((failedRequest) => failedRequest.type === causedBy.type)) {
            dispatch(removeFailedRequestActionFromState(causedBy))
        }
    }

    if (isApiFailureAction(action)) {
        const { dispatch } = store
        const { type, message, status } = action.payload
        const { causedBy } = action.meta

        const isResponseBodyEmpty =
            message === UNEXPECTED_END_OF_JSON_INPUT1 || message === UNEXPECTED_END_OF_JSON_INPUT2

            if ([401, 403].includes(status || 0)) {
                attemptTokenRefreshOnAuthError(store, status)
                    .then((refreshSuccess) => {
                        if (!refreshSuccess && !isResponseBodyEmpty) {
                            const notificationMessage = getNotificationMessage(type, message, causedBy)
                            dispatch(
                                createNotification({
                                    type: notificationMessage === 'error.sessionExpired' ? 'warning' : 'error',
                                    message: notificationMessage,
                                }),
                            )
                        }
                    })
                .catch(() => {
                    if (!isResponseBodyEmpty) {
                        const notificationMessage = getNotificationMessage(type, message, causedBy)
                        dispatch(
                            createNotification({
                                type: notificationMessage === 'error.sessionExpired' ? 'warning' : 'error',
                                message: notificationMessage,
                            }),
                        )
                    }
                })
        } else if (!isResponseBodyEmpty) {
            const notificationMessage = getNotificationMessage(type, message, causedBy)
            dispatch(
                createNotification({
                    type: notificationMessage === 'error.sessionExpired' ? 'warning' : 'error',
                    message: notificationMessage,
                }),
            )
        }

        if (message === SESSION_EXPIRED || message === SESSION_COOKIE_MISSING) {
            if (message === SESSION_COOKIE_MISSING) {
                const mayRetry = sessionIdManager.recordRecoveryAttempt()
                if (!mayRetry) {
                    logger.error('Session recovery attempts exhausted, stopping retry loop')
                    dispatch(
                        createNotification({
                            type: 'error',
                            message: 'error.sessionExpired',
                        }),
                    )
                    return
                }
                sessionIdManager.clear()
            }

            attemptTokenRefreshOnAuthError(store, 401)
                .then((refreshSuccess) => {
                    if (refreshSuccess) {
                        logger.info('Session expired: tokens refreshed, fetching entry data to recreate session')
                        sessionIdManager.resetRecoveryAttempts()
                        dispatch(fetchEntryData())
                        dispatch(triggerE3DReinit())
                    } else {
                        dispatch(resetConfigurationProcess(undefined))
                        dispatch(fetchEntryData())
                    }
                })
                .catch(() => {
                    dispatch(resetConfigurationProcess(undefined))
                    dispatch(fetchEntryData())
                })
        }

        const requestsToRetry: string[] = [
            fetchEntryData,
            fetchCarlineData,
            fetchCarlineFiltersData,
            fetchEfficiencyData,
            fetchVisualization,
            fetchCart,
            fetchEquipmentGroups,
        ].map((requestToRetry) => getType(requestToRetry))

        if (requestsToRetry.includes(causedBy.type)) {
            dispatch(
                addFailedRequestActionToState({
                    type: causedBy.type,
                    error: action?.payload,
                }),
            )
        }
    }
}

export default errorMiddleware
